import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { Core } from '../_core/core';
import { Metering } from '../_core/metering';
import { Spinner } from '../_core/spinner';
import { ToastService } from '../_core/toast.service';
import { IMeter, ISettlementSimulation } from '../core.interface';

const AF_SUPPLY_TYPE_ID = 1;
const AF_COLOR = '#448AFF';

@Component({
  selector: 'app-simulacion-liquidacion',
  imports: [
    ButtonModule,
    CardModule,
    CurrencyPipe,
    DatePipe,
    DatePickerModule,
    DecimalPipe,
    InputNumberModule,
    LucideAngularModule,
    MessageModule,
    ReactiveFormsModule,
    SelectModule,
    SkeletonModule,
    TableModule,
  ],
  templateUrl: './simulacion-liquidacion.html',
  styleUrl: './simulacion-liquidacion.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimulacionLiquidacion implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly $core = inject(Core);
  private readonly $metering = inject(Metering);
  private readonly $toast = inject(ToastService);
  private readonly spinner = inject(Spinner);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  private readonly resultHeading = viewChild<ElementRef<HTMLElement>>('resultHeading');
  private readonly readingField = viewChild<ElementRef<HTMLElement>>('readingField');

  readonly afColor = AF_COLOR;
  readonly afMeters = signal<IMeter[]>([]);
  readonly selectedMeter = signal<IMeter | null>(null);
  readonly simulation = signal<ISettlementSimulation | null>(null);
  readonly isDownloading = signal(false);
  readonly isSimulating = signal(false);
  readonly loaded = signal(false);
  readonly readingValue = signal<number | null>(null);
  readonly helpExpanded = signal(false);
  readonly minSimulationDate = signal<Date | null>(null);

  readonly showMeterSelect = computed(() => this.afMeters().length > 1);
  readonly hasAfMeters = computed(() => this.afMeters().length > 0);
  readonly canSimulate = computed(() => {
    const meter = this.selectedMeter();
    return meter != null && meter.lastReadingValue != null;
  });

  readonly estimatedConsumption = computed(() => {
    const meter = this.selectedMeter();
    const reading = this.readingValue();
    if (meter?.lastReadingValue == null || reading == null) {
      return null;
    }
    const consumption = Number(reading) - Number(meter.lastReadingValue);
    return consumption > 0 ? consumption : 0;
  });

  readonly resultPeriod = computed(() => {
    const reading = this.simulation()?.readings?.[0];
    if (!reading) {
      return null;
    }
    return {
      from: reading.previousReadingDate,
      to: reading.readingDate,
      consumption: reading.consumption,
    };
  });

  readonly form = this.fb.nonNullable.group({
    meterId: [null as number | null, Validators.required],
    readingDate: [new Date() as Date | null, Validators.required],
    reading: [null as number | null, Validators.required],
  });

  ngOnInit(): void {
    this.form.controls.reading.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.readingValue.set(value));

    this.form.controls.meterId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((meterId) => {
        const meter = this.afMeters().find((m) => m.meterId === meterId) ?? null;
        this.applySelectedMeter(meter);
      });

    void this.loadData();
  }

  async loadData(): Promise<void> {
    const tenantId = this.$core.selectedBuilding()?.tenantId;
    if (!tenantId) {
      this.loaded.set(true);
      return;
    }

    this.spinner.show();
    try {
      const meters = await this.$metering.getMeters(tenantId, AF_SUPPLY_TYPE_ID);
      this.afMeters.set(meters);

      if (meters.length === 1) {
        this.form.controls.meterId.setValue(meters[0].meterId);
      }
    } catch {
      this.$toast.error('No se pudieron cargar los contadores de agua fría.');
      this.afMeters.set([]);
    } finally {
      this.spinner.hide();
      this.loaded.set(true);
    }
  }

  meterLabel(meter: IMeter): string {
    const serial = meter.serialNumber ?? meter.serialNumber1 ?? String(meter.meterId);
    return meter.location ? `${serial} — ${meter.location}` : serial;
  }

  toggleHelp(): void {
    this.helpExpanded.update((v) => !v);
  }

  goHome(): void {
    void this.router.navigate(['/home']);
  }

  resetSimulation(): void {
    this.simulation.set(null);
    this.form.controls.reading.reset(null, { emitEvent: true });
    this.readingValue.set(null);
    queueMicrotask(() => {
      const host = this.readingField()?.nativeElement;
      const input = host?.querySelector('input');
      input?.focus();
    });
  }

  private applySelectedMeter(meter: IMeter | null): void {
    this.selectedMeter.set(meter);
    this.simulation.set(null);
    this.form.controls.reading.reset(null, { emitEvent: true });
    this.form.controls.readingDate.setValue(new Date());

    this.form.controls.reading.clearValidators();
    this.form.controls.readingDate.clearValidators();

    if (meter?.lastReadingValue != null) {
      this.form.controls.reading.setValidators([
        Validators.required,
        (c) => this.validateReading(c, meter.lastReadingValue!),
      ]);
    } else {
      this.form.controls.reading.setValidators([Validators.required]);
    }

    if (meter?.lastReadingDate) {
      this.form.controls.readingDate.setValidators([
        Validators.required,
        (c) => this.validateReadingDate(c, meter.lastReadingDate!),
      ]);
      this.minSimulationDate.set(this.computeMinSimulationDate(meter.lastReadingDate));
    } else {
      this.form.controls.readingDate.setValidators([Validators.required]);
      this.minSimulationDate.set(null);
    }

    this.form.controls.reading.updateValueAndValidity({ emitEvent: false });
    this.form.controls.readingDate.updateValueAndValidity({ emitEvent: false });
    this.readingValue.set(null);
  }

  private validateReading(control: AbstractControl, lastReading: number): ValidationErrors | null {
    if (control.value == null || control.value === '') {
      return null;
    }
    return Number(control.value) >= lastReading ? null : { tooLow: true };
  }

  private validateReadingDate(
    control: AbstractControl,
    lastReadingDate: string,
  ): ValidationErrors | null {
    const selected = this.toDateOnly(control.value);
    const lastReading = this.toDateOnly(lastReadingDate);
    if (!selected || !lastReading) {
      return null;
    }
    return selected.getTime() > lastReading.getTime() ? null : { dateTooEarly: true };
  }

  private computeMinSimulationDate(lastReadingDate: string): Date | null {
    const lastReading = this.toDateOnly(lastReadingDate);
    if (!lastReading) {
      return null;
    }
    const min = new Date(lastReading);
    min.setDate(min.getDate() + 1);
    return min;
  }

  private toDateOnly(value: unknown): Date | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    if (typeof value === 'string') {
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
      if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      }
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      }
    }
    return null;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private focusResult(): void {
    queueMicrotask(() => {
      const el = this.resultHeading()?.nativeElement;
      if (!el) {
        return;
      }
      el.focus({ preventScroll: true });
      el.scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  async simulate(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.$toast.warning(
        'Revisa la fecha y la lectura: la fecha debe ser posterior a la última lectura liquidada.',
      );
      return;
    }
    if (!this.canSimulate()) {
      this.$toast.warning('No hay una lectura liquidada previa para simular.');
      return;
    }

    const tenantId = this.$core.selectedBuilding()?.tenantId;
    const { meterId, reading, readingDate } = this.form.getRawValue();
    if (!tenantId || meterId == null || reading == null || !readingDate) {
      return;
    }

    this.isSimulating.set(true);
    this.spinner.show();
    this.simulation.set(null);
    try {
      const result = await this.$metering.createSettlementSimulation(
        tenantId,
        meterId,
        reading,
        this.formatDate(readingDate),
      );
      const services = [...(result.services ?? [])].sort((a, b) => a.order - b.order);
      this.simulation.set({ ...result, services });
      this.focusResult();
    } catch (err: unknown) {
      this.$toast.error(
        await this.resolveErrorMessage(err, 'Ha ocurrido un error al simular tu liquidación.'),
      );
    } finally {
      this.isSimulating.set(false);
      this.spinner.hide();
    }
  }

  downloadPdf(): void {
    const tenantId = this.$core.selectedBuilding()?.tenantId;
    const simulationId = this.simulation()?.simulationId;
    if (!tenantId || !simulationId) {
      this.$toast.warning('No hay una liquidación simulada para descargar.');
      return;
    }

    this.isDownloading.set(true);
    this.spinner.show();
    this.$metering.downloadSettlementSimulation(tenantId, simulationId).subscribe({
      next: async (blob) => {
        try {
          if (await this.isJsonErrorBlob(blob)) {
            const message = await this.readBlobErrorMessage(
              blob,
              'Ha ocurrido un error al descargar la liquidación.',
            );
            this.$toast.error(message);
            return;
          }

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Liquidacion_simulada_${simulationId}.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
        } catch {
          this.$toast.error('Ha ocurrido un error al descargar la liquidación.');
        } finally {
          this.isDownloading.set(false);
          this.spinner.hide();
        }
      },
      error: async (err: unknown) => {
        this.isDownloading.set(false);
        this.spinner.hide();
        this.$toast.error(
          await this.resolveErrorMessage(err, 'Ha ocurrido un error al descargar la liquidación.'),
        );
      },
    });
  }

  private async resolveErrorMessage(err: unknown, fallback: string): Promise<string> {
    if (!(err instanceof HttpErrorResponse)) {
      return fallback;
    }

    const body = err.error;
    if (typeof body?.message === 'string' && body.message.trim()) {
      return body.message;
    }
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body instanceof Blob) {
      return this.readBlobErrorMessage(body, fallback);
    }

    return fallback;
  }

  private async isJsonErrorBlob(blob: Blob): Promise<boolean> {
    if (!blob || blob.size === 0) {
      return true;
    }
    const type = blob.type?.toLowerCase() ?? '';
    if (type.includes('json') || type.includes('text')) {
      return true;
    }
    if (type.includes('pdf') || type.includes('octet-stream')) {
      return false;
    }
    const text = await blob.slice(0, 32).text();
    return text.trimStart().startsWith('{');
  }

  private async readBlobErrorMessage(blob: Blob, fallback: string): Promise<string> {
    try {
      const text = await blob.text();
      if (!text.trim()) {
        return fallback;
      }
      const parsed = JSON.parse(text) as { message?: string };
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message;
      }
    } catch {
      // keep fallback
    }
    return fallback;
  }
}
